import React, { useMemo } from 'react';
import {
  MsalProvider as MsalReactProvider,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from '@azure/msal-browser';
import { createMsalConfig } from './msalConfig';

interface Props {
  children: React.ReactNode;
}

/**
 * Wraps the application with MSAL's MsalProvider.
 * Initializes the PublicClientApplication and sets the active account
 * on successful login events.
 */
export const AppMsalProvider: React.FC<Props> = ({ children }) => {
  const msalInstance = useMemo(() => {
    const config = createMsalConfig();
    const instance = new PublicClientApplication(config);

    // Set active account on login success
    instance.addEventCallback((event: EventMessage) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload
      ) {
        const result = event.payload as AuthenticationResult;
        if (result.account) {
          instance.setActiveAccount(result.account);
        }
      }
    });

    // If accounts already exist (from cache), set the first one active
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }

    return instance;
  }, []);

  return (
    <MsalReactProvider instance={msalInstance}>
      {children}
    </MsalReactProvider>
  );
};
